import { useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Sparkline } from "@/components/charts/sparkline";
import { DeltaBadge } from "@/components/panels/delta-badge";
import { percentDelta, deltaDirection } from "@/lib/analytics/delta";
import type { WorkflowStats } from "@/lib/analytics/types";
import { formatMs } from "@/lib/utils/format-duration";

interface WorkflowsTableProps {
  rows: WorkflowStats[];
  /** When provided, adds a Δ runs column matched by workflowId. */
  compareStats?: WorkflowStats[];
}

/** Prefers an em-dash over "0s" for empty cells in the table layout. */
function formatDurOrDash(ms: number): string {
  return ms <= 0 ? "—" : formatMs(ms);
}

export function WorkflowsTable({ rows, compareStats }: WorkflowsTableProps) {
  const hasCompare = compareStats != null && compareStats.length > 0;

  // Index compare stats by workflowId for O(1) lookup.
  const compareById = useMemo(() => {
    if (!compareStats) return new Map<number, WorkflowStats>();
    return new Map(compareStats.map((s) => [s.workflowId, s]));
  }, [compareStats]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Workflows</CardTitle>
        <CardDescription>Per-workflow activity over the window</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead className="text-right">Runs</TableHead>
                {hasCompare ? (
                  <TableHead className="text-right" title="% change in runs vs previous period">
                    Δ runs
                  </TableHead>
                ) : null}
                <TableHead className="text-right">Success %</TableHead>
                <TableHead className="text-right">Avg duration</TableHead>
                <TableHead className="text-right">p95 duration</TableHead>
                <TableHead className="text-right">Avg queue</TableHead>
                <TableHead className="text-right">Rerun %</TableHead>
                <TableHead className="text-right">Minutes</TableHead>
                <TableHead>Trend</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={hasCompare ? 10 : 9} className="text-center text-muted-foreground">
                    No workflows found.
                  </TableCell>
                </TableRow>
              ) : null}
              {rows.map((row) => {
                const cmp = compareById.get(row.workflowId);
                // If exists in A but not B → previous = 0 → delta = 0%, direction neutral.
                const prevRuns = cmp?.runs ?? 0;
                const delta = percentDelta(row.runs, prevRuns);
                const direction = deltaDirection("totalRuns", row.runs, prevRuns);
                // totalRuns is neutral by semantic, but for Δ runs column we still show
                // the magnitude for informational purposes (direction stays neutral).

                return (
                  <TableRow key={row.workflowId}>
                    <TableCell>
                      <div className="font-medium">{row.name}</div>
                      <div className="text-xs text-muted-foreground font-mono">{row.path}</div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{row.runs.toLocaleString()}</TableCell>
                    {hasCompare ? (
                      <TableCell className="text-right">
                        <DeltaBadge value={delta} direction={direction} />
                      </TableCell>
                    ) : null}
                    <TableCell className="text-right tabular-nums">
                      {(row.successRate * 100).toFixed(1)}%
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{formatDurOrDash(row.avgDurationMs)}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatDurOrDash(row.p95DurationMs)}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatDurOrDash(row.avgQueueTimeMs)}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {(row.rerunRate * 100).toFixed(1)}%
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{row.estimatedMinutes.toLocaleString()}</TableCell>
                    <TableCell>
                      <Sparkline data={row.periodTrend} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
